import React from 'react';
import { Box, Heading, Button } from '@indeed/ifl-components';
import { Link, useNavigate } from 'react-router-dom';
import { usePageFlow } from '../../lib/pageFlow/usePageFlow';

export interface NavigationProps {
    currentStep?: string;
}

// Navigation labels are now retrieved from centralized flow definitions

const Navigation: React.FunctionComponent<NavigationProps> = ({ currentStep }) => {
    const { allSteps, currentStep: flowCurrentStep } = usePageFlow();
    const navigate = useNavigate();

    // Use provided currentStep or fall back to flow state
    const activeStep = currentStep || flowCurrentStep || 'contact-info';

    // Generate navigation steps from all steps
    const navigationSteps = React.useMemo(() => {
        if (!allSteps) {
            return [];
        }

        return allSteps
            .filter((step) => !step.routes.includes('/post-apply')) // Hide success page from navigation
            .map((step) => ({
                routes: step.routes, // Keep all routes for active step matching
                primaryRoute: step.routes[0], // Primary route for display/navigation
                label: step.label || step.routes[0], // Use centralized label from flow definition
                href: step.routes[0],
                isCompleted: step.isCompleted,
            }));
    }, [allSteps]);

    // Check if a step is active (activeStep matches any of the step's routes)
    const isStepActive = (stepRoutes: string[]) => stepRoutes.includes(activeStep);

    return (
        <Box
            as="nav"
            sx={{
                backgroundColor: 'background.default',
                borderBlockEnd: '1px solid',
                borderColor: 'border.subtle',
                padding: 4,
                position: 'sticky',
                insetBlockStart: 0,
                zIndex: 10,
            }}
        >
            <Box
                sx={{
                    maxInlineSize: '80rem',
                    margin: '0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                {/* Logo/Brand */}
                <Heading
                    as="h1"
                    level={3}
                    sx={{
                        color: 'primary.default',
                        margin: 0,
                    }}
                >
                    <Link
                        to="/"
                        style={{
                            textDecoration: 'none',
                            color: 'inherit',
                        }}
                    >
                        Indeed Application
                    </Link>
                </Heading>

                {/* Step Navigation - Using flow state */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                    }}
                >
                    {navigationSteps.map((step, index) => (
                        <Box
                            key={step.primaryRoute}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minInlineSize: '2rem',
                                    blockSize: '2rem',
                                    borderRadius: '50%',
                                    backgroundColor: isStepActive(step.routes)
                                        ? 'primary.default'
                                        : step.isCompleted
                                          ? 'success.default'
                                          : 'background.secondary',
                                    color:
                                        isStepActive(step.routes) || step.isCompleted
                                            ? 'background.default'
                                            : 'foreground.secondary',
                                    fontSize: 1,
                                    fontWeight: 'bold',
                                }}
                            >
                                {step.isCompleted ? '✓' : index + 1}
                            </Box>
                            <Link
                                to={step.href}
                                style={{
                                    color: isStepActive(step.routes)
                                        ? '#2557a7' // primary.default fallback
                                        : step.isCompleted
                                          ? '#0a8f47' // success.default fallback
                                          : '#757575', // foreground.secondary fallback
                                    fontWeight: isStepActive(step.routes) ? 'bold' : 'normal',
                                    textDecoration: 'none',
                                }}
                            >
                                {step.label}
                            </Link>
                            {index < navigationSteps.length - 1 && (
                                <Box
                                    sx={{
                                        inlineSize: '1rem',
                                        blockSize: '1px',
                                        backgroundColor: 'border.subtle',
                                        marginInlineStart: 2,
                                    }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Link
                        to="/"
                        style={{
                            color: '#757575',
                            textDecoration: 'none',
                        }}
                    >
                        Save & Exit
                    </Link>
                    <Button variant="secondary" onClick={() => void navigate(-1)}>
                        Help
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default Navigation;
